import React from 'react'
import { AuthProvider, PendoProvider, FeatureFlagsProvider } from '@/context'
import { ChatContainer } from '@/components/chat'
import { useChat, useAuthContext, useFeatureFlags } from '@/hooks'

/**
 * Main chat application component.
 * Handles authentication, chat state, and renders the chat UI.
 */
function ChatApp() {
  const { getToken, isLoading: isAuthLoading, error: authError } = useAuthContext()
  const { suggested_chips_enabled, careadvocate_callback_scheduling_enabled } = useFeatureFlags()


  const {
    messages,
    isLoading,
    sendMessage,
    handleWidgetAction,
    voteOnMessage,
    isVoting,
  } = useChat({
    getAuthToken: getToken,
    enabled: !isAuthLoading && !authError,
  })

  // Show loading state while authenticating
  if (isAuthLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div
            className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"
            role="status"
            aria-label="Loading"
          />
          <p className="mt-4 text-sm text-gray-600">Connecting...</p>
        </div>
      </div>
    )
  }

  // Show error if auth failed
  if (authError) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div
          className="max-w-md rounded-lg border border-error-200 bg-error-50 p-6 text-center"
          role="alert"
        >
          <p className="font-medium text-error-800">Connection Error</p>
          <p className="mt-2 text-sm text-error-700">{authError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-error-600 px-4 py-2 text-sm font-medium text-white hover:bg-error-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen">
      <ChatContainer
        messages={messages}
        isLoading={isLoading}
        onSendMessage={sendMessage}
        onWidgetAction={handleWidgetAction}
        onVote={voteOnMessage}
        isVoting={isVoting}
        suggestedChipsEnabled={suggested_chips_enabled}
        careAdvocateCallbackSchedulingEnabled={careadvocate_callback_scheduling_enabled}
        title="Lantern Digital Assistant ᴬᴵ"
      />
    </div>
  )
}

/**
 * Root App component with providers.
 */
export default function App() {
  // Dev-only debug page accessible via /#ui
  if (import.meta.env.DEV && window.location.hash === '#ui') {
    const ComponentShowcase = React.lazy(() => import('@/pages/debug').then(m => ({ default: m.ComponentShowcase })))

    return (
      <AuthProvider>
        <PendoProvider>
          <React.Suspense fallback={<div className="flex h-screen items-center justify-center">Loading...</div>}>
            <ComponentShowcase />
          </React.Suspense>
        </PendoProvider>
      </AuthProvider>
    )
  }

  return (
    <AuthProvider>
      <PendoProvider>
        <FeatureFlagsProvider>
          <ChatApp />
        </FeatureFlagsProvider>
      </PendoProvider>
    </AuthProvider>
  )
}
